import './App.css';
import { useEffect, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import OnboardingRouter from 'route/OnboardingRouter';
import { AuthContextProvider } from 'utils/AuthContext';
import ScrollToTop from 'components/ScrollToTop';
import { IdleTimerProvider, useIdleTimer } from 'react-idle-timer';
import { DialogTitle, Grid, DialogContent, Button, Typography } from '@mui/material';
import PinInput from 'react-pin-input';
import { checkPin, getSibling } from 'store/slices/userSlice';
import { useAppDispatch } from 'store/hooks';
import Offline from 'offline';
import { getAuthChannel } from './utils/broadcastAuth';
import { StyledDialog } from './theme';
import {
  consumeIdleTimedOut,
  markIdleTimedOut,
  markPatientSelectionPending,
} from 'components/SiblingSwitch/patientSelection';

function App() {
  let ele: PinInput | null;
  const [showDialog, setShowDialog] = useState(false);
  const [changeDialogText, setChangeDialogText] = useState(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [showOffline, setShowOffline] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const channel = getAuthChannel();
    if (!channel) return;

    channel.onmessage = (event) => {
      if (event.data?.type === 'TOKEN_UPDATE') {
        sessionStorage.setItem('access-token', event.data.accessToken);
        sessionStorage.setItem('refresh-token', event.data.refreshToken);
      }

      if (event.data?.type === 'LOGOUT') {
        sessionStorage.clear();
        window.location.href = '/signout';
      }
    };

    return () => {
      channel.close();
    };
  }, []);

  const { getIdleTime } = useIdleTimer({
    timeout: 1000 * 60 * 10,
    name: 'idle-timer',
    syncTimers: 0,
    leaderElection: false,
  });

  useEffect(() => {
    if (navigator.onLine === false) {
      setShowOffline(true);
    }

    if (window.location.pathname === '/') {
      localStorage.removeItem('onIdle');
    }
    const onIdle = localStorage.getItem('onIdle');
    window.addEventListener('storage', function (e) {
      if (e.key === 'onIdle' && e.newValue === null && window.location.pathname !== '/') {
        window.location.href = '/signout';
      }
    });

    if (onIdle === '1') {
      setShowDialog(true);
    } else if (onIdle === '2') {
      setShowDialog(true);
      setChangeDialogText(true);
    }
  }, [showDialog]);

  useEffect(() => {
    window.addEventListener('visibilitychange', () => {
      const session = sessionStorage.getItem('user');
      const user = session && JSON.parse(session);
      if (
        document.visibilityState === 'visible' &&
        window.location.pathname !== '/' &&
        window.location.pathname !== '/signup' &&
        window.location.pathname !== '/migration/signup' &&
        window.location.pathname !== '/transfer/signup' &&
        window.location.pathname !== '/emailverification' &&
        window.location.pathname !== '/profile/insurance' &&
        window.location.pathname !== '/documents' &&
        window.location.pathname !== '/policy/sign/documents' &&
        !user?.isAdmin
      ) {
        localStorage.setItem('onIdle', '2');
        setShowDialog(true);
        setChangeDialogText(true);
      }
    });
    const onIdle = localStorage.getItem('onIdle');
    if (onIdle === '1') {
      setInterval(() => {
        const result = getIdleTime();
        if (result >= 86400000) {
          setShowDialog(false);
          localStorage.setItem('onIdle', '0');
          window.location.href = '/signout';
        }
      }, 60000);
    }
  }, [getIdleTime, showDialog]);

  const onIdle = () => {
    const session = sessionStorage.getItem('user');
    const user = session && JSON.parse(session);
    if (
      window.location.pathname !== '/' &&
      window.location.pathname !== '/signup' &&
      window.location.pathname !== '/migration/signup' &&
      window.location.pathname !== '/transfer/signup' &&
      window.location.pathname !== '/profile/insurance' &&
      !user?.isAdmin
    ) {
      localStorage.setItem('onIdle', '1');
      markIdleTimedOut();
      setShowDialog(true);
    }
  };

  /**
   * Reloads the current page after a correct PIN. MM-1172: after an idle timeout, a guardian with
   * more than one child profile first gets the patient picker, shown over the reloaded page.
   */
  const reloadAfterPin = () => {
    const reload = () => window.location.replace(window.location.href);
    if (!consumeIdleTimedOut()) {
      reload();
      return;
    }
    dispatch(getSibling({}))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200 && (response?.data?.length ?? 0) > 1) {
          markPatientSelectionPending('idle');
        }
      })
      .catch(() => undefined)
      .finally(reload);
  };

  const handleOnComplete = (value: string) => {
    const data = {
      pin: value,
    };

    dispatch(checkPin(data))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setShowDialog(false);
          localStorage.setItem('onIdle', '0');
          reloadAfterPin();
          return;
        }
        if (response?.status === 401) {
          setShowError(true);
          setTimeout(() => {
            setShowError(false);
          }, 2000);
          if (ele) {
            ele.clear();
          }
        }
      });
  };

  const handleLogout = () => {
    setShowDialog(false);
    localStorage.removeItem('onIdle');
    window.location.href = '/signout';
  };

  return (
    <AuthContextProvider>
      {showOffline ? (
        <Offline />
      ) : (
        <>
          <Router>
            <IdleTimerProvider timeout={1000 * 60 * 10} onIdle={onIdle}>
              <ScrollToTop />
              <OnboardingRouter />
            </IdleTimerProvider>
          </Router>
          <StyledDialog aria-labelledby='simple-dialog-title' open={showDialog} sx={{ zIndex: 9999 }}>
            <DialogTitle id='simple-dialog-title' sx={{ padding: '16px 24px 0' }}>
              {!changeDialogText ? 'Session Timeout' : ''}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={0} direction='column' alignItems='center' justifyContent='center'>
                {changeDialogText && <Typography>Your session was paused due to inactivity.</Typography>}
                <Typography>Please re-enter your 6-digit PIN to continue.</Typography>

                <Grid item xs={3} mt={'20px'}>
                  <PinInput
                    length={6}
                    focus
                    secret
                    secretDelay={500}
                    type='numeric'
                    inputMode='numeric'
                    onComplete={handleOnComplete}
                    inputStyle={{
                      border: '1px solid #FFC44D',
                      borderRadius: '8px',
                      margin: '10px 15px',
                    }}
                    ref={(n) => (ele = n)}
                  />
                </Grid>
                {showError ? <Typography color={'red'}> Invalid PIN. Please try again. </Typography> : ''}
                <Typography variant='h3' mt='12px'>
                  OR
                </Typography>
                <Grid container justifyContent='center'>
                  <Button variant='text' onClick={handleLogout} sx={{ textAlign: 'right' }}>
                    {' '}
                    LOG IN AGAIN{' '}
                  </Button>
                </Grid>
              </Grid>
            </DialogContent>
          </StyledDialog>
        </>
      )}
    </AuthContextProvider>
  );
}

export default App;
